import { ApiType } from '@/types';
import axios, { AxiosResponse } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

type Data = Record<string, unknown>;

// AxiosError を握り潰さず、ステータス・レスポンスボディ(422時の { errors: [...] } 等)を
// 呼び出し元が参照できるようにする。message は互換性のため従来と同じ形式を維持する。
export class ApiError extends Error {
  readonly status?: number;
  readonly body: unknown;

  constructor(status: number | undefined, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class Api {
  static async get<T>(
    path: string,
    type: ApiType,
    params: Data = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axios.get<T>(`${apiDomain(type)}/${path}`, {
        params: snakecaseKeys(params),
        headers: headers(),
      });
      const data = response.data as unknown as Record<string, unknown>;
      return {
        ...response,
        data: camelcaseKeys(data, { deep: true }) as T,
      };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new ApiError(
          e.response?.status,
          e.response?.data,
          `AxiosError: ${e.message} [${type}] GET /${path}`
        );
      }
      throw new Error(
        `API Error: ${e instanceof Error ? e.message : 'Unknown error'}`
      );
    }
  }

  // TODO(okubo): Promise<any>をaxios returnでgenericsに変更
  static async post<T>(
    path: string,
    type: ApiType,
    data: Data = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axios.post<Data>(
        `${apiDomain(type)}/${path}`,
        snakecaseKeys(data, { deep: true }),
        {
          headers: headers(),
        }
      );
      return {
        ...response,
        data: camelcaseKeys(response.data, { deep: true }) as T,
      };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new ApiError(
          e.response?.status,
          e.response?.data,
          `AxiosError: ${e.message} [${type}] POST /${path}`
        );
      }
      throw new Error(
        `API Error: ${e instanceof Error ? e.message : 'Unknown error'}`
      );
    }
  }

  static async postFormData<T>(
    path: string,
    type: ApiType,
    data: FormData
  ): Promise<AxiosResponse<T>> {
    const formDataHeaders = { 'X-CSRF-TOKEN': headers()['X-CSRF-TOKEN'] };
    try {
      const response = await axios.post<Record<string, unknown>>(
        `${apiDomain(type)}/${path}`,
        data,
        { headers: formDataHeaders }
      );
      return {
        ...response,
        data: camelcaseKeys(response.data, { deep: true }) as T,
      };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new ApiError(
          e.response?.status,
          e.response?.data,
          `AxiosError: ${e.message} [${type}] POST(FormData) /${path}`
        );
      }
      throw new Error(
        `API Error: ${e instanceof Error ? e.message : 'Unknown error'}`
      );
    }
  }

  static async patch<T>(
    path: string,
    type: ApiType,
    data: Data = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axios.patch(
        `${apiDomain(type)}/${path}`,
        snakecaseKeys(data),
        { headers: headers() }
      );
      return {
        ...response,
        data: camelcaseKeys(response.data, { deep: true }) as T,
      };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new ApiError(
          e.response?.status,
          e.response?.data,
          `AxiosError: ${e.message} [${type}] PATCH /${path}`
        );
      }
      throw new Error(
        `API Error: ${e instanceof Error ? e.message : 'Unknown error'}`
      );
    }
  }

  static async delete<T>(
    path: string,
    type: ApiType,
    data: Data = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axios.delete<Data>(`${apiDomain(type)}/${path}`, {
        data: snakecaseKeys(data),
        headers: headers(),
      });
      return {
        ...response,
        data: camelcaseKeys(response.data, { deep: true }) as T,
      };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new ApiError(
          e.response?.status,
          e.response?.data,
          `AxiosError: ${e.message} [${type}] DELETE /${path}`
        );
      }
      throw new Error(
        `API Error: ${e instanceof Error ? e.message : 'Unknown error'}`
      );
    }
  }
}

const headers = () => {
  let token = '';
  const csrfTokenElem = document.querySelector('[name=csrf-token]');

  if (csrfTokenElem) {
    token = csrfTokenElem.getAttribute('content') ?? '';
  }

  return {
    'Content-Type': 'application/json',
    'X-CSRF-TOKEN': token,
  };
};

const apiDomain = (apiType: ApiType): string => {
  switch (apiType) {
    case 'common':
      return COMMON_API_DOMAIN;
    case 'customer':
      return CUSTOMER_API_DOMAIN;
    case 'partner':
      return PARTNER_API_DOMAIN;
    default:
      return '';
  }
};

// NOTE(okubo): process.env.RAILS_ENVで分けてたけど、不要なので一旦変更で
const COMMON_API_DOMAIN: string = (() => {
  return `${window.location.origin}/api`;
})();

// NOTE(okubo): process.env.RAILS_ENVで分けてたけど、不要なので一旦変更で
const CUSTOMER_API_DOMAIN: string = (() => {
  return `${window.location.origin}/api_customer`;
})();

const PARTNER_API_DOMAIN: string = (() => {
  return `${window.location.origin}/api_partner`;
})();
